import type { SchedulePortion } from '@/utils/scheduleBoard';

const NO_CHANGE = 0;

export interface PortionEdit {
  portion: SchedulePortion;
  quantity: number;
}

export interface OverbookedPortion {
  typeCode: string;
  by: number;
}

// A block's quantity lives one row per capacity type, and each row is its own PATCH, so an
// edit is only ever the portions the draft actually moved. Drafts are keyed by assignment id
// because that is what identifies the row the write targets.
export function changedPortions(portions: SchedulePortion[], draft: Record<string, number>): PortionEdit[] {
  return portions.reduce<PortionEdit[]>((edits, portion) => {
    const quantity = draft[portion.assignmentId];
    if (typeof quantity !== 'number' || !Number.isFinite(quantity) || quantity === portion.quantity) {
      return edits;
    }
    return [...edits, { portion, quantity }];
  }, []);
}

// Only an increase can overbook: lowering a quantity in an already-overbooked week reduces
// the deficit, and prompting to confirm an overbook there would be nonsense. Mirrors the rule
// the capacity drawer's editor already applies.
export function overbookedPortions(portions: SchedulePortion[], draft: Record<string, number>, remainingByRowId: Record<string, number>): OverbookedPortion[] {
  return changedPortions(portions, draft).reduce<OverbookedPortion[]>((over, { portion, quantity }) => {
    const delta = quantity - portion.quantity;
    const remaining = remainingByRowId[portion.rowId] ?? NO_CHANGE;
    if (delta <= NO_CHANGE || delta <= remaining) {
      return over;
    }
    return [...over, { typeCode: portion.typeCode, by: delta - remaining }];
  }, []);
}
